import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, Paper, Typography, Divider, RadioGroup, FormControlLabel, 
    Radio, Button, Chip, Grid, TextField, Checkbox, FormGroup, InputAdornment 
} from '@material-ui/core';
import { useNotify, useRedirect } from 'react-admin';
import scalesProvider from '../../provider/scales.provider';

// --- IMPORTACIONES RECUPERADAS ---
import DibujoImg from '../../assets/image/Dibujo.png';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import TodayIcon from '@material-ui/icons/Today';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline'; // Icono para el profesional

import { useGetIdentity } from "ra-core";
import { ScaleNote } from './ScaleNote';
import { ScaleResult } from './Results/ScaleResult';
import Spinner from '../../components/Spinner';

import './ScaleForm.css';

// --- LÓGICA DE CÁLCULO SÍNCRONO (Mantenida) ---
const calculateTotalScoreSync = (questions, answers) => {
    let total = 0;
    questions.forEach(q => {
        const answer = answers[q.id];
        if (answer === undefined || answer === null || answer === '') return;

        const options = q.questionOptionScores || [];
        if (Array.isArray(answer)) { 
            answer.forEach(val => {
                const opt = options.find(o => String(o.scaleOptionId || o.scaleOption?.id || o.id) === String(val));
                if (opt) total += (Number(opt.score) || 0);
            });
        } else {
            const selectedOpt = options.find(o => String(o.scaleOptionId || o.scaleOption?.id || o.id) === String(answer));
            if (selectedOpt) {
                total += (Number(selectedOpt.score) || 0);
            } else if (!isNaN(answer)) { 
                total += Number(answer);
            }
        }
    });
    return total;
};

export const ScaleForm = (props) => {
    const notify = useNotify();
    const redirect = useRedirect();
    const [loading, setLoading] = useState(true);
    const { identity } = useGetIdentity();
    const [disabled, setDisabled] = useState(() => {
        const initialRegister = props?.isNew ? props?.data?.register : props?.data;
        const finished = initialRegister?.scaleStatusName && initialRegister.scaleStatusName !== 'En curso';
        return !!props?.isDisabled || finished;
    });
    const [scale, setScale] = useState(null);
    const [register, setRegister] = useState(() => props?.isNew ? props?.data?.register : props?.data);
    const [answers, setAnswers] = useState({});
    const [startedAt, setStartedAt] = useState(null);
    const [openScaleNote, setOpenScaleNote] = useState(false);
    const [openScaleResult, setOpenScaleResult] = useState(false);
    const [activeSpinner, setActiveSpinner] = useState(false);
    
    const computedResultRef = useRef(null);
    const [, setComputedResult] = useState(null);

    const isPrivilegedUser = identity?.professionalValue === 'ADMIN' || identity?.professionalValue === 'SUPER_ADMIN';
    const effectiveDisabled = disabled && !isPrivilegedUser;

    // --- EFECTOS ---
    useEffect(() => {
        const finished = register?.scaleStatusName && register.scaleStatusName !== 'En curso';
        setDisabled(!!props?.isDisabled || finished);
    }, [props?.isDisabled, register?.scaleStatusName]);

    useEffect(() => {
        setRegister(props?.isNew ? props?.data?.register : props?.data);
    }, [props?.isNew, props?.data]);

    useEffect(() => {
        if (!register || !scale) return;
        const next = {};
        const questions = Array.isArray(scale?.questions) ? scale.questions : [];
        (register.scaleResponses || []).forEach(r => {
            const q = questions.find(q => q.id === r.scaleQuestionId);
            if (!q) return;
            const type = (q.responseType || '').toLowerCase();
            if (type === 'choice' || ((q.questionOptionScores || []).length > 0 && type !== 'check' && type !== 'number')) {
                if (r.scaleOptionId != null) next[q.id] = String(r.scaleOptionId);
            } else if (type === 'check') {
                if (r.scaleOptionId != null) next[q.id] = [String(r.scaleOptionId)];
            } else if (type === 'text' || type === 'date') {
                if (r.textValue != null) next[q.id] = r.textValue;
            } else if (type === 'number') {
                if (r.numericValue != null) next[q.id] = String(r.numericValue);
            }
        });
        setAnswers(prev => ({ ...prev, ...next }));
    }, [register, scale]);

    useEffect(() => {
        setStartedAt(register?.createdDate);
    }, [register?.createdDate]);

    useEffect(() => {
        const fetchScale = async () => {
            const sid = !props?.isNew ? props?.data?.scaleId : props?.data?.id;
            if (!sid) { setLoading(false); return; }
            const res = await scalesProvider.getScaleDefinition(null, { id: sid });
            if (res && res.data) setScale(res.data);
            setLoading(false);
        }
        fetchScale();
    }, [props]);

    // --- LOGICA DE ACTUALIZACIÓN ---
    const sendUpdate = async (question, partial) => {
        if (effectiveDisabled) return;
        const responsesMap = {};
        (register?.scaleResponses || []).forEach(r => { if (r) responsesMap[r.scaleQuestionId] = r; });
        const r = responsesMap[question?.id];
        const dto = {
            id: r?.id, 
            scaleQuestionId: question.id,
            scaleOptionId: null, textValue: null, numericValue: null, score: null,
            order: question.order ?? 0,
            ...partial,
        };
        try { await scalesProvider.updatedResponse(null, dto); } catch (err) {}
    };

    const handleChoiceChange = (q) => (e) => {
        if (effectiveDisabled) return;
        const val = e.target.value;
        setAnswers(prev => ({ ...prev, [q.id]: val }));
        const optionId = parseInt(val, 10);
        const opt = q.questionOptionScores?.find(o => String(o.scaleOptionId || o.scaleOption?.id || o.id) === val);
        const score = !Number.isNaN(optionId) ? (opt?.score ?? null) : null;
        sendUpdate(q, { scaleOptionId: Number.isNaN(optionId) ? null : optionId, score });
    };

    const handleCheckToggle = (q, opt) => (e) => {
        if (effectiveDisabled) return;
        const val = String(opt.scaleOptionId || opt.scaleOption?.id || opt.id);
        setAnswers(prev => {
            const prevArr = Array.isArray(prev[q.id]) ? prev[q.id] : [];
            const next = e.target.checked ? [...prevArr, val] : prevArr.filter(v => v !== val);
            if (e.target.checked) {
                const optionId = parseInt(val, 10);
                sendUpdate(q, { scaleOptionId: optionId, score: opt?.score ?? null });
            } else {
                sendUpdate(q, { scaleOptionId: null, score: null });
            }
            return { ...prev, [q.id]: next };
        });
    };

    const textUpdateTimers = useRef({});
    const handleTextChange = (q) => (e) => {
        if (effectiveDisabled) return;
        const textVal = e.target.value;
        setAnswers(prev => ({ ...prev, [q.id]: textVal }));
        if (textUpdateTimers.current[q.id]) clearTimeout(textUpdateTimers.current[q.id]);
        textUpdateTimers.current[q.id] = setTimeout(() => {
            sendUpdate(q, { textValue: textVal });
        }, 600);
    };

    const handleNumberChange = (q) => (e) => {
        if (effectiveDisabled) return;
        const raw = e.target.value;
        const numVal = raw === '' ? null : Number(raw);
        if (raw !== '' && !Number.isFinite(numVal)) return;

        // Búsqueda dinámica del score máximo para validación
        const maxScore = (q.questionOptionScores && q.questionOptionScores.length > 0)
            ? Math.max(...q.questionOptionScores.map(opt => opt.score || 0))
            : null;

        if (scale?.code === "MMSE" && numVal !== null && maxScore) {
            if (numVal < 0 || numVal > maxScore) {
                notify('El valor debe estar entre 0 y ' + maxScore + '.', { type: 'warning' });
                return;
            }
        }
        setAnswers(prev => ({ ...prev, [q.id]: raw }));
        const scoreToSend = (scale?.code === "MMSE" && Number.isFinite(numVal)) ? parseInt(numVal) : numVal;
        sendUpdate(q, { numericValue: numVal, score: scoreToSend });
    };

    const handleDateChange = (q) => (e) => {
        if (effectiveDisabled) return;
        const dateVal = e.target.value;
        setAnswers(prev => ({ ...prev, [q.id]: dateVal }));
        sendUpdate(q, { textValue: dateVal });
    };

    // --- GUARDADO FINAL ---
    const handlefinishScaleRegister = async (currentRegister, score) => {
        try {
            const formatForApi = (dateStr) => {
                if (!dateStr) return null;
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return null;
                const pad = (n) => String(n).padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            };

            const dto = { 
                scaleRegisterId: currentRegister.id, 
                totalScore: String(score),
                createdDate: formatForApi(currentRegister.createdDate) 
            };

            const res = await scalesProvider.finishScaleRegister(null, dto);
            const updatedRegister = res?.data || res;
            if (updatedRegister) {
                setRegister(updatedRegister);
                notify(`Escala finalizada correctamente. Puntuación: ${score}`, { type: 'info' });
                setDisabled(true);
            }
        } catch (e) {
            notify('Error al finalizar el test', { type: 'error' });
        }
    };

    const handleFinishAndCalculate = async () => {
        if (!register || !scale) return;
        if (!validateAllAnswered()) return;
        setActiveSpinner(true);
        const freshScore = calculateTotalScoreSync(scale.questions, answers);
        await handlefinishScaleRegister(register, freshScore);
        setActiveSpinner(false);
        setOpenScaleResult(true);
    };

    const handleComputedResult = (result) => {
        computedResultRef.current = result; 
        setComputedResult(result); 
    };

    const handleDialogScaleNote = (isOpen) => setOpenScaleNote(isOpen);
    const handleScaleResult = (isOpen) => setOpenScaleResult(isOpen);

    // --- RENDERIZADO ---
    const getQuestionsByFactor = () => {
        if (!scale) return [];
        const factors = scale.factors || [];
        const questions = (scale.questions || []).sort((a, b) => (a.order - b.order));
        if (!factors || factors.length === 0) return [{ factor: null, questions }];

        const hasScaleFactorId = questions.some(q => q.scaleFactorId);
        const hasFactorCode = questions.some(q => q.factorCode);

        if (hasScaleFactorId || hasFactorCode) {
            return factors.sort((a, b) => a.order - b.order).map(f => ({
                factor: f,
                questions: questions.filter(q => (hasScaleFactorId ? q.scaleFactorId === f.id : q.factorCode === f.code))
            }));
        }
        const perFactor = Math.ceil(questions.length / Math.max(1, factors.length));
        return factors.map((f, idx) => ({
            factor: f,
            questions: questions.slice(idx * perFactor, (idx + 1) * perFactor)
        }));
    };

    const renderControlForQuestion = (q) => {
        const type = (q.responseType || '').toLowerCase();
        const d = effectiveDisabled;

        if (type === 'choice') {
            return (
                <RadioGroup style={{ display: 'flex', flexDirection: 'column', gap: 4 }} value={answers[q.id] || ''} onChange={handleChoiceChange(q)}>
                    {(q.questionOptionScores || []).slice().sort((a, b) => a.order - b.order).map(opt => (
                        <FormControlLabel key={opt.id} value={String(opt.scaleOptionId || opt.scaleOption?.id || opt.id)} control={<Radio color="primary" disabled={d} />} label={opt?.scaleOption?.label || ''} disabled={d} />
                    ))}
                </RadioGroup>
            );
        }
        if (type === 'check') {
            const selected = Array.isArray(answers[q.id]) ? answers[q.id] : [];
            return (
                <FormGroup row>
                    {(q.questionOptionScores || []).slice().sort((a, b) => a.order - b.order).map(opt => (
                        <FormControlLabel key={opt.id} control={<Checkbox color="primary" checked={selected.includes(String(opt.scaleOptionId || opt.scaleOption?.id || opt.id))} onChange={handleCheckToggle(q, opt)} disabled={d} />} label={opt?.scaleOption?.label || ''} disabled={d} />
                    ))}
                </FormGroup>
            );
        }
        if (type === 'text') {
            const textVal = answers[q.id] || '';
            const lineBreaks = (textVal.match(/\n/g) || []).length;
            const showHelper = (scale?.code === 'ISAACS') || (q?.code === 'ISAACS');
            return <TextField fullWidth variant="outlined" size="small" multiline rows={3} value={textVal} onChange={handleTextChange(q)} helperText={showHelper ? `Saltos de línea: ${lineBreaks}` : undefined} disabled={d} />;
        }
        if (type === 'number') {
            return (
                <FormGroup row>
                     {(q.questionOptionScores || []).length > 0 ? (
                        q.questionOptionScores.map(opt => (
                            <FormControlLabel key={opt.id} control={<TextField type="number" variant="outlined" size="small" value={answers[q.id] || ''} onChange={handleNumberChange(q)} disabled={d} />} label={opt.label || ''} disabled={d} />
                        ))
                     ) : (
                        <TextField type="number" fullWidth variant="outlined" size="small" value={answers[q.id] || ''} onChange={handleNumberChange(q)} disabled={d} />
                     )}
                </FormGroup>
            );
        }
        if (type === 'date') {
            return <TextField type="date" fullWidth variant="outlined" size="small" InputLabelProps={{ shrink: true }} value={answers[q.id] || ''} onChange={handleDateChange(q)} disabled={d} />;
        }
        if ((q.questionOptionScores || []).length > 0) {
            return (
                <RadioGroup row value={answers[q.id] || ''} onChange={handleChoiceChange(q)}>
                    {q.questionOptionScores.map(opt => (
                        <FormControlLabel key={opt.id} value={String(opt.scaleOptionId || opt.scaleOption?.id || opt.id)} control={<Radio color="primary" disabled={d} />} label={opt.label || ''} disabled={d} />
                    ))}
                </RadioGroup>
            );
        }
        return null;
    };

    const isQuestionAnswered = (q) => {
        if (q.isVisible === false) return true;
        const val = answers[q.id];
        const type = (q.responseType || '').toLowerCase();
        if (type === 'check') return Array.isArray(val) && val.length > 0;
        if (type === 'number') return val !== undefined && val !== null && val !== '';
        if (type === 'text' || type === 'date') return val && val.trim() !== '';
        return !!val;
    };

    const validateAllAnswered = () => {
        const missing = (scale?.questions || []).filter(q => !isQuestionAnswered(q));
        if (missing.length) {
            notify('Por favor completa todas las preguntas antes de calcular.', { type: 'warning' });
            return false;
        }
        return true;
    };

    if (loading) return <Box p={2}><Typography>Cargando escala...</Typography></Box>;
    if (!scale) return <Box p={2}><Typography>No se pudo cargar la escala.</Typography></Box>;

    const displayClientName = register?.clientName || `${props?.record?.name || ''} ${props?.record?.surnames || ''}`.trim();
    // Recuperamos el nombre del profesional (Prioriza fullName para mayor robustez)
    const displayProfessionalName = register?.professionalName || identity?.fullName || identity?.name || '';
    // Formato de fecha para input type date
    const displayEvalDate = register?.createdDate ? new Date(register.createdDate).toISOString().slice(0, 10) : '';

    return (
        <Paper elevation={0} style={{ padding: 16 }} className="sf-root">
            {/* CABECERA (Estilo Original) */}
            <Box mb={4}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>{scale.name}</Typography>
                <Typography variant="body2" style={{ color: '#5f6b7a' }}>
                    Tiempo estimado: {scale?.estimatedTime || '—'} min · Items: {scale?.items || scale?.questions?.length || '—'}
                </Typography>
            </Box>

            {/* CAJA DE DATOS (Estilo Original con 3 items en el Grid) */}
            <Paper variant="outlined" style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, borderColor: '#ECEFF1' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F4F6F8' }}>
                    <PersonOutlineIcon style={{ color: '#003488' }} />
                    <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#003488' }}>Datos Persona Usuaria</Typography>
                </Box>
                <Box p={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Nombre Persona Usuaria"
                                variant="filled"
                                fullWidth
                                disabled
                                value={displayClientName}
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: (<InputAdornment position="start"><PersonOutlineIcon style={{ color: '#9E9E9E' }} /></InputAdornment>),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Fecha de Evaluación"
                                variant="filled"
                                fullWidth
                                type="date"
                                disabled={effectiveDisabled} // Solo Admin edita
                                value={displayEvalDate}
                                onChange={(e) => {
                                    // Actualiza estado local, se guarda al Calcular
                                    const newVal = e.target.value;
                                    setRegister(prev => ({ ...prev, createdDate: newVal ? `${newVal}T12:00:00` : null }));
                                }}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: (<InputAdornment position="start"><TodayIcon style={{ color: '#9E9E9E' }} /></InputAdornment>),
                                }}
                            />
                        </Grid>
                        {/* RESTAURADO: Campo del Profesional en su sitio original */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Nombre del Profesional"
                                variant="filled"
                                fullWidth
                                disabled
                                value={displayProfessionalName}
                                InputProps={{
                                    disableUnderline: true,
                                    startAdornment: (<InputAdornment position="start"><WorkOutlineIcon style={{ color: '#9E9E9E' }} /></InputAdornment>),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {!openScaleResult && !activeSpinner && (
                <>
                    {getQuestionsByFactor().map((s, si) => {
                        const factorColors = ['#003488', '#00ab8e'];
                        const factorBg = ['#eaf0ff', '#e7f8f4'];
                        const color = factorColors[si % factorColors.length];
                        const bg = factorBg[si % factorBg.length];
                        const hasFactor = !!s.factor;

                        return (
                            <Paper key={s.factor?.id || `sec-${si}`} variant="outlined" style={{ marginBottom: 24, borderRadius: 12, overflow: 'hidden', borderColor: '#e0e7ff' }}>
                                {hasFactor ? (
                                    <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: bg, borderLeft: `6px solid ${color}` }}>
                                        <Chip size="small" label={s.factor?.code || `F${si + 1}`} style={{ backgroundColor: color, color: '#fff', fontWeight: 600 }} />
                                        <Typography variant="subtitle1" style={{ fontWeight: 700, color }}>{s.factor?.name || `Factor ${si + 1}`}</Typography>
                                    </Box>
                                ) : (
                                    <Box style={{ padding: '12px 16px', background: '#F4F6F8' }}>
                                        <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#003488' }}>Preguntas</Typography>
                                    </Box>
                                )}
                                <Box p={2}>
                                    {s.questions.map((q) => (
                                        <Box key={q.id} mb={2}>
                                            {q.isVisible !== false && (
                                                <>
                                                    <Typography variant="subtitle1" style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ fontWeight: 600 }}>
                                                            {`${
                                                                scale?.code === 'ET' 
                                                                    ? (q.order >= 5 ? q.order - 2 : q.order === 4 ? 3 : q.order) 
                                                                    : scale?.code === 'MMSE'
                                                                        ? (q.order > 5 ? q.order - 2 : q.order > 3 ? q.order - 1 : q.order)
                                                                        : q.order
                                                            }. ${q.text}`}
                                                        </span>
                                                        {scale?.code !== '' && (
                                                            <Typography component="span" style={{ marginLeft: 'auto', fontWeight: 700, fontSize: '1.2rem' }}>
                                                                {/* Cálculo dinámico del Score Máximo */}
                                                                {q.questionOptionScores && q.questionOptionScores.length > 0 
                                                                    ? Math.max(...q.questionOptionScores.map(opt => opt.score || 0)) 
                                                                    : 0}
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    {q.subText && <Typography variant="body2" style={{ color: '#5f6b7a', marginBottom: 6 }}>{q.subText}</Typography>}
                                                </>
                                            )}
                                            {scale?.code === 'MMSE' && q?.code === 'q13' && (
                                                <Box mt={1} mb={1}>
                                                    <img src={DibujoImg} alt="Dibujo a copiar" className="sf-img" />
                                                    <Typography variant="caption" style={{ color: '#5f6b7a', display: 'block', marginTop: 6 }}>Dibujo a copiar</Typography>
                                                </Box>
                                            )}
                                            {renderControlForQuestion(q)}
                                            <Divider style={{ marginTop: 12 }} />
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        );
                    })}

                    <Box className="sf-actions">
                        <Box>
                            {startedAt && <Typography variant="caption" style={{ color: '#5f6b7a' }}>Inicio: {new Date(startedAt).toLocaleString()}</Typography>}
                        </Box>
                        <Box className="sf-action-buttons">
                            <Button variant="outlined" onClick={() => props.onCancel()} style={{ marginRight: 8 }}>Volver</Button>
                            <Button variant="outlined" onClick={() => setOpenScaleNote(true)} style={{ marginRight: 8 }}>Añadir Notas</Button>
                            {disabled && (
                                <Button variant="outlined" onClick={() => redirect(`/objectives?filter={"clientName":"${displayClientName}"}`)} style={{ marginRight: 8 }}>Objetivos</Button>
                            )}
                            <Button color="primary" variant="contained" onClick={handleFinishAndCalculate}>
                                {disabled ? 'Ver ' : 'Calcular '} resultado
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            <ScaleNote {...props} openScaleNote={openScaleNote} handleDialogScaleNote={setOpenScaleNote} onSaveSuccess={handleFinishAndCalculate} />
            
            <Box style={{ display: (openScaleResult && !activeSpinner) ? 'block' : 'none' }}>
                <ScaleResult 
                    {...props} 
                    disabled={disabled} 
                    register={register} 
                    identity={identity} 
                    data={scale} 
                    handleDialogScaleNote={handleDialogScaleNote} 
                    handleScaleResult={handleScaleResult} 
                    onComputedResult={handleComputedResult} 
                />
            </Box>
            
            {activeSpinner && <Spinner />}
        </Paper>
    );
};

export default ScaleForm;